import * as React from 'react';
import { Button, Label, makeStyles, mergeClasses, ProgressBar, shorthands, tokens } from '@fluentui/react-components';
import { Delete24Regular, Document24Regular } from '@fluentui/react-icons';
import { useDebouncedCallback } from 'use-debounce';
import { FormFieldDescription } from '../FormFieldDescription';

interface Props extends Omit<React.ComponentProps<typeof FormFieldDescription>, 'id'> {
  label?: React.ReactNode;
  required?: boolean;
  /**
   * mimetype file type acception list
   * @default *
   */
  accept?: string;
  /**
   * Disabled the input
   */
  disabled?: boolean;
  /**
   * Indication that the file is currently submitting/uploading. Animates the `Progress` bar
   */
  isSubmitting?: boolean;
  /**
   * What locale the file size should be formatted with
   */
  locale: string;
  /**
   * Whether or not to allow adding multiple files
   * @default false
   */
  multiple?: boolean;
  /**
   * Report the file change back to a controlling component
   */
  onChangeFile?: (files: FileList | null) => void;
  /**
   * Pre-set the file. Can be overridden
   */
  value?: FileList;
}

export function FileField({
  accept,
  hint,
  validationMessage,
  validationState,
  disabled,
  isSubmitting,
  label,
  locale,
  multiple,
  onChangeFile,
  required,
  value,
}: Props) {
  const id = React.useId();
  const [focused, setFocus] = React.useState(false);
  const [hovered, setHovered] = React.useState(false);
  const [files, setFiles] = React.useState<FileList | null>(value || null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const classes = useStyles();

  const focus = React.useCallback(() => setFocus(true), []);
  const hover = React.useCallback(() => setHovered(true), []);
  const blur = React.useCallback(() => setFocus(false), []);
  const unhover = useDebouncedCallback(() => setHovered(false), 100);

  React.useEffect(() => {
    document.addEventListener('dragenter', hover);
    document.addEventListener('dragover', hover);
    document.addEventListener('dragleave', unhover);

    return () => {
      document.removeEventListener('dragenter', hover);
      document.removeEventListener('dragover', hover);
      document.removeEventListener('dragleave', unhover);
    };
  }, [hover, unhover]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files || null;
    setFiles(files);
    onChangeFile && onChangeFile(files);
  }

  return (
    <div className={classes.root}>
      <Label
        htmlFor={id}
        required={required}
        className={mergeClasses(
          classes.labelText,
          !files && classes.labelNoFile,
          !files && !disabled && (focused || hovered) && classes.focused
        )}
      >
        {label}
        <input
          accept={accept}
          aria-describedby={hint ? `desc-${id}` : undefined}
          aria-errormessage={validationState === 'error' ? `error-${id}` : undefined}
          disabled={disabled}
          id={id}
          multiple={multiple}
          onBlur={blur}
          onChange={handleChange}
          onFocus={focus}
          onMouseEnter={hover}
          onMouseLeave={unhover}
          ref={inputRef}
          required
          tabIndex={0}
          type="file"
          className={classes.fileInput}
        />
      </Label>
      {files
        ? Array.from(files).map((file, i) => (
            <FileView
              key={i}
              disabled={disabled}
              isSubmitting={isSubmitting}
              file={file}
              locale={locale}
              onRemove={() => {
                const data = new DataTransfer();
                for (let i = 0; i < files.length; i++) {
                  if (file !== files[i]) {
                    data.items.add(files[i]);
                  }
                }

                const newFiles = data.files.length ? data.files : null;
                setFiles(newFiles);
                onChangeFile && onChangeFile(newFiles);

                if (inputRef.current) {
                  inputRef.current.files = data.files;
                }
              }}
            />
          ))
        : null}
      <FormFieldDescription
        hint={hint}
        validationMessage={validationMessage}
        validationState={validationState}
        id={id}
      />
    </div>
  );
}

interface FileProps {
  disabled?: boolean;
  file: File;
  isSubmitting?: boolean;
  locale: string;
  onRemove: () => void;
}

function FileView({ disabled, file, isSubmitting = false, locale, onRemove }: FileProps) {
  const classes = useStyles();

  const [unit, divisor] = Object.entries(conversion).find(([, value]) => file.size > value) || [
    'megabyte',
    conversion['megabyte'],
  ];
  const formatter = new Intl.NumberFormat(locale, { style: 'unit', unit });

  return (
    <div className={classes.fileRoot}>
      <div className={classes.fileIcon}>
        <Document24Regular />
      </div>
      <div className={classes.progress}>
        <div className={classes.progressContent}>
          <span>{file.name}</span>
          <span>{formatter.format(Math.round((file.size / divisor) * 10) / 10)}</span>
        </div>
        <ProgressBar value={isSubmitting ? undefined : 1} />
      </div>
      <Button
        className={classes.deleteButton}
        disabled={disabled}
        aria-label={`Remove ${file.name}`}
        icon={<Delete24Regular />}
        onClick={(event) => {
          event.stopPropagation();
          event.preventDefault();
          onRemove && onRemove();
        }}
      />
    </div>
  );
}

const conversion = {
  megabyte: 1_048_576,
  kilobyte: 1_024,
};

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  labelNoFile: {
    display: 'flex',
    flexDirection: 'row',
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding(tokens.spacingVerticalXL, tokens.spacingHorizontalXXL),
    cursor: 'pointer',
  },
  focused: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  labelText: {
    position: 'relative',
    fontWeight: tokens.fontWeightRegular,
  },
  fileInput: {
    opacity: 0,
    cursor: 'pointer',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  fileRoot: {
    display: 'flex',
    flexDirection: 'row',
    ...shorthands.gap(tokens.spacingHorizontalL),
  },
  fileIcon: {
    height: '49px',
    width: '49px',

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    ...shorthands.borderRadius(tokens.borderRadiusXLarge),
  },
  progress: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
  },
  progressContent: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    ...shorthands.padding(
      tokens.spacingVerticalNone,
      tokens.spacingHorizontalNone,
      tokens.spacingVerticalM,
      tokens.spacingHorizontalNone
    ),
  },
  deleteButton: {
    color: tokens.colorBrandForeground1,
    ':hover': {
      color: tokens.colorBrandForeground1,
    },
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    ...shorthands.border(tokens.borderRadiusNone),
    height: '38px',
    width: '38px',
    maxWidth: '38px',
  },
});
